'use client'
import { useState } from 'react'
import { ToolProgress } from '@/components/tools/tool-progress'
import { Loader2, AlertCircle, Flame, Check, X, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { NewsletterCapture } from '@/components/marketing/newsletter-capture'

type AuditRow = { id: string; label: string; passed: boolean; evidence: string; gated: boolean }
type AuditResult = { url: string; finalUrl: string; title: string; score: number; outOf: number; rows: AuditRow[]; fetchedAt: string }
type RoastFix = { id: string; priority: number; title: string; roast: string; fix: string }
type DeterministicRoast = { score: number; verdict: string; fixes: RoastFix[] }
type RoastResponse = { audit: AuditResult; deterministic: DeterministicRoast; aiRoast: string | null; aiEnabled: boolean }

export function RoastMyPdp() {
  const [url, setUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<RoastResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!url.trim()) return
    setLoading(true)
    setError(null)
    setData(null)
    try {
      const res = await fetch('/api/tools/roast-my-pdp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim() }),
      })
      const json = await res.json()
      if (!res.ok) setError(json?.error || 'Roast failed. Try a different URL.')
      else setData(json as RoastResponse)
    } catch {
      setError('Network error. Try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="w-full max-w-3xl">
      <form onSubmit={onSubmit} className="flex flex-col gap-3 sm:flex-row">
        <Input
          type="url"
          inputMode="url"
          required
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Paste a product page URL (brace yourself)"
          className="h-12 flex-1 text-base"
          aria-label="Product page URL"
        />
        <Button type="submit" size="lg" disabled={loading} className="h-12 px-6">
          {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Roasting</> : <>Roast it <Flame className="ml-2 h-4 w-4" /></>}
        </Button>
      </form>

      {loading && (
        <ToolProgress label="your product page" steps={[{ title: "Fetching your page", sub: "Loading the PDP HTML (SSRF-guarded)" }, { title: "Running the CRO audit", sub: "Images, social proof, CTA, trust, copy" }, { title: "Writing the roast", sub: "Prioritised, specific, a little mean" }]} />
      )}

      {error && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {data && (
        <div className="mt-8 space-y-6">
          {/* Headline + verdict */}
          <div className="rounded-2xl border-2 border-orange-500/30 bg-gradient-to-br from-orange-500/[0.07] to-card p-6 sm:p-8">
            <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-orange-600 dark:text-orange-400">
                  <Flame className="h-3.5 w-3.5" /> The roast
                </p>
                <p className="mt-1 truncate font-display text-base font-medium" title={data.audit.finalUrl}>
                  {data.audit.title || data.audit.finalUrl}
                </p>
              </div>
              <div className="flex shrink-0 items-baseline gap-1">
                <span className="font-display text-6xl font-bold tabular-nums text-foreground">{data.deterministic.score}</span>
                <span className="text-2xl text-muted-foreground">/ 100</span>
              </div>
            </div>
            <p className="mt-4 text-[15px] font-medium leading-relaxed text-foreground/90">{data.deterministic.verdict}</p>
          </div>

          {/* AI roast when available */}
          {data.aiEnabled && data.aiRoast && (
            <div className="rounded-2xl border-2 border-primary/25 bg-gradient-to-br from-primary/[0.06] to-card p-5 sm:p-6">
              <p className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.22em] text-primary">
                <Sparkles className="h-3.5 w-3.5" /> AI roast
              </p>
              <p className="mt-3 whitespace-pre-wrap text-[15px] leading-relaxed text-foreground/90">{data.aiRoast}</p>
            </div>
          )}

          {/* Deterministic prioritised fixes */}
          {data.deterministic.fixes.length > 0 ? (
            <div>
              <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                Fix these, in this order
              </p>
              <ol className="mt-3 grid gap-3">
                {data.deterministic.fixes.map((f, i) => (
                  <li key={f.id} className="rounded-xl border bg-background p-4">
                    <div className="flex items-start gap-3">
                      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-orange-500/15 font-display text-xs font-bold text-orange-700 dark:text-orange-300">
                        {i + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium leading-snug">{f.title}</p>
                        <p className="mt-1 text-sm text-foreground/80">{f.roast}</p>
                        <p className="mt-2 flex items-start gap-1.5 text-sm text-muted-foreground">
                          <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                          <span>{f.fix}</span>
                        </p>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          ) : (
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5 text-sm">
              <p className="font-medium text-foreground">Nothing to roast. Every audit signal passed. Annoyingly good page.</p>
            </div>
          )}

          {/* Full audit signal list */}
          <details className="group rounded-2xl border bg-card p-5 sm:p-6 [&_summary::-webkit-details-marker]:hidden">
            <summary className="flex cursor-pointer list-none items-center justify-between font-display text-base font-medium">
              <span>See all {data.audit.outOf} audit signals</span>
              <span className="transition group-open:rotate-45">+</span>
            </summary>
            <ul className="mt-4 grid gap-2.5">
              {data.audit.rows.map((r) => (
                <li key={r.id} className="flex items-start gap-3 rounded-xl border bg-background p-3.5">
                  {r.passed ? (
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"><Check className="h-3 w-3" /></span>
                  ) : (
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rose-500/15 text-rose-700 dark:text-rose-300"><X className="h-3 w-3" /></span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium leading-snug">{r.label}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{r.evidence}</p>
                  </div>
                </li>
              ))}
            </ul>
          </details>

          {!data.aiEnabled && (
            <div className="rounded-xl border border-dashed bg-secondary/40 p-4 text-xs text-muted-foreground">
              The sharper AI roast is off on this deployment, so you are seeing the deterministic version, which is built from the same audit signals and is just as actionable. Set ANTHROPIC_API_KEY to enable the AI layer.
            </div>
          )}

          <div className="rounded-2xl border bg-card p-5 sm:p-6">
            <NewsletterCapture
              source="tool:roast-my-pdp"
              headline="Get the PDP teardown checklist + the next roast"
              caption="One email with the full PDP fix checklist this roast runs on, plus new teardowns when they ship. No drip sequence."
              cta="Send it over"
            />
            <p className="mt-4 text-center text-xs text-muted-foreground">
              Want the fixes done for you?{' '}
              <a href="https://app.idukki.io/register" className="font-medium text-primary underline underline-offset-4">Start a free Idukki trial</a>.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
